import * as dates from "./dates";
import moment from "moment";

export const daysInMonth = (y, m) => {
  switch (m) {
    case 2:
      if (y % 400 === 0 || (y % 4 === 0 && y % 100 !== 0)) {
        return 29;
      }
      return 28;
    case 4:
    case 6:
    case 9:
    case 11:
      return 30;
    default:
      return 31;
  }
};

export const getDateStringFromDateTimeString = (datetimeString) => {
  if (!datetimeString) {
    return null;
  } else {
    const dateString = moment.parseZone(datetimeString).format("YYYY-MM-DD");
    return dateString === "Invalid date" ? null : dateString;
  }
};

export const validatedDateStr = (dateStr) => {
  if (dateStr == null) {
    return null;
  }
  if (isNaN(new Date(dateStr))) {
    return null;
  }
  return dateStr;
};

export const formatDate = (date) => {
  if (!date) return "";
  if (validatedDateStr(date) == null) return "";
  return new Intl.DateTimeFormat("en-us", {
    timeZone: "UTC",
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(date));
};

export const formatDateTime = (date) => {
  if (!date) return "";
  if (validatedDateStr(date) == null) return "";
  return (
    new Intl.DateTimeFormat("en-us", {
      timeZone: "UTC",
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(date)) + " UTC"
  );
};

export const formatDateWithFormat = (date, formatStr) => {
  if (!date) return "";
  if (!formatStr || typeof formatStr !== "string") return formatDate(date);
  const parsed = moment.parseZone(date);
  if (!parsed.isValid()) return formatDate(date);
  const formatted = parsed.format(formatStr.trim());
  return formatted === "Invalid date" ? formatDate(date) : formatted;
};

export const strDateToYearMonthDay = (date) => {
  if (validatedDateStr(date) == null) {
    return null;
  }

  return {
    year: parseInt(date.substring(0, 4)),
    month: parseInt(date.substring(5, 7)),
    day: parseInt(date.substring(8, 10)),
  };
};

export const strDateToMonthsFloat = (date) => {
  const dateParts = strDateToYearMonthDay(date);

  if (dateParts == null) {
    return null;
  }

  const whole = dateParts.year * 12 + dateParts.month - 1;
  const frac =
    (dateParts.day - 1) / daysInMonth(dateParts.year, dateParts.month);

  return whole + frac;
};

export const strDateInRange = (date, start, end) => {
  date = strDateToYearMonthDay(date);
  start = strDateToYearMonthDay(start);
  end = strDateToYearMonthDay(end);

  if (date == null || start == null || end == null) {
    return null;
  }

  const datePartsToInt = (dateParts) => {
    return dateParts.year * 10_000 + dateParts.month * 100 + dateParts.day;
  };

  const dateInt = datePartsToInt(date);
  const startInt = datePartsToInt(start);
  const endInt = datePartsToInt(end);

  return dateInt >= startInt && dateInt < endInt;
};

export const getDate = () => {
  return new Date();
};

export const getLastDateStringOfMonth = (dateString) => {
  return moment.parseZone(dateString).endOf("month").format("YYYY-MM-DD");
};

export const getFirstDateStringOfNextMonth = (dateString) => {
  return moment
    .parseZone(dateString)
    .endOf("month")
    .add(1, "days")
    .format("YYYY-MM-DD");
};

export const getFirstOfNextMonth = () => {
  const now = dates.getDate();
  return new Date(now.getUTCFullYear(), now.getUTCMonth() + 1, 1);
};

export const getFirstOfNextMonthStrNoTimeZone = () => {
  return getFirstDateStringOfNextMonth(
    moment.parseZone(dates.getDate()).format("YYYY-MM-DD")
  );
};

export const getMoveForwardOneYear = (date) => {
  if (!date) {
    return undefined;
  }
  const d = moment.parseZone(date);
  // The last day of February maps to the last day of February in the next
  // year even across leap-year boundaries; every other date keeps its month
  // and day (moment clamps 29 February to 28 February in a non-leap year).
  // 28 February of a leap year is not the last day, so it maps to 28
  // February — the previous +1 day / +1 year / -1 day trick returned the
  // 27th for that input.
  const isLastDayOfFebruary = d.month() === 1 && d.date() === d.daysInMonth();
  if (isLastDayOfFebruary) {
    return d.add(1, "year").endOf("month").format("YYYY-MM-DD");
  }
  return d.add(1, "year").format("YYYY-MM-DD");
};

export const adjustDate = (date, { days = 0, years = 0 } = {}) => {
  if (
    validatedDateStr(date) == null ||
    typeof days !== "number" ||
    typeof years != "number"
  ) {
    return null;
  }
  return moment
    .parseZone(date)
    .add(days ?? 0, "day")
    .add(years ?? 0, "year")
    .format("YYYY-MM-DD");
};

export const toISODateString = (date) => {
  if (!date) {
    return undefined;
  }
  return moment.parseZone(date).format("YYYY-MM-DD");
};

export const parseDateValue = (value) => {
  if (!value) {
    return null;
  }
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value !== "string") {
    return null;
  }
  const trimmed = value.trim();
  const isBareDate = /^\d{4}-\d{2}-\d{2}$/.test(trimmed);
  const parsed = new Date(isBareDate ? `${trimmed}T00:00:00` : trimmed);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const yearsBetween = (inceptionStr, expirationStr) => {
  const expiration = moment(expirationStr);
  const inception = moment(inceptionStr);
  if (expiration.isBefore(inception)) {
    return yearsBetween(expirationStr, inceptionStr);
  }
  const years = expiration.diff(inception, "years");
  const a = inception.clone().add(years, "years");
  if (a.isSame(expiration)) {
    return years;
  }
  const b = inception.clone().add(years + 1, "years");
  const denomDays = a.diff(b, "days");
  const numDays = a.diff(expiration, "days");
  return parseFloat((years + numDays / denomDays).toFixed(5));
};

export const parseDateStringFromDateOrYear = (
  input,
  {
    defaultMMDD = "01-01",
    minYear = 999,
    maxYear = 10000,
    defaultReturn = null,
    locale = "default",
    allowLocalFormats = false,
  }
) => {
  const [defaultMonth, defaultDay] = defaultMMDD.split("-");
  if (!input) return defaultReturn;
  var d = 0;
  var m = 0;
  var y = 0;
  const startMonth =
    new Intl.DateTimeFormat(locale).formatToParts()[0].type === "month";
  function two(val) {
    const startZero = "00" + val;
    return startZero.substring(startZero.length - 2);
  }

  if (typeof input !== "string") input = "" + input;

  if (/^\d{4}$/.test(input)) input += "-" + defaultMonth + "-" + defaultDay;
  if (/^\d{4}-\d{1,2}$/.test(input)) input += "-" + defaultDay;

  const split = input.split(/[-./]/);

  function setDM_MD() {
    if ((startMonth && Number(split[0]) <= 12) || Number(split[1]) > 12) {
      d = split[1];
      m = split[0];
    } else {
      d = split[0];
      m = split[1];
    }
  }
  if (/^\d{4}-\d{1,2}-\d{1,2}$/.test(input)) {
    y = split[0];
    m = split[1];
    d = split[2];
  } else if (
    allowLocalFormats &&
    /^\d{1,2}[-./]\d{1,2}[-./]\d{2}$/.test(input)
  ) {
    setDM_MD();
    y = "20" + split[2];
  } else if (
    allowLocalFormats &&
    /^\d{1,2}[-./]\d{1,2}[-./]\d{4}$/.test(input)
  ) {
    setDM_MD();
    y = split[2];
  } else return defaultReturn;
  const retVal = y + "-" + two(m) + "-" + two(d);
  if (isNaN(new Date(retVal))) return defaultReturn;
  if (Number(y) < minYear || Number(y) > maxYear) return defaultReturn;
  return retVal;
};

const datesMatch = (date1, date2) =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

export const readableDate = (timestamp, { includeTime = false } = {}) => {
  const date = new Date(timestamp);
  const time = includeTime ? `, ${date.toLocaleTimeString()}` : "";

  const today = new Date();
  if (datesMatch(date, today)) {
    return `Today${time}`;
  }

  let yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (datesMatch(date, yesterday)) {
    return `Yesterday${time}`;
  }

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  return `${date.toLocaleDateString(undefined, options)}${time}`;
};

export const asOfDate = (timestamp, { includeDayOfWeek = false } = {}) => {
  if (!timestamp) return "";
  const parsed = moment(timestamp);
  return parsed.isValid()
    ? parsed.format(
        `${includeDayOfWeek ? "dddd " : ""}MMMM D, YYYY [at] h:mm:ss A`
      )
    : "";
};

export const inYear = (dateStr, yearInt) => {
  if (validatedDateStr(dateStr) == null) {
    return false;
  } else if (yearInt != null && typeof yearInt !== "number") {
    yearInt = parseInt(yearInt);
  } else {
    yearInt = yearInt ?? new Date().getFullYear();
  }

  return isFinite(yearInt)
    ? moment.parseZone(dateStr).year() === yearInt
    : false;
};

export const dateName = (date, { placeholder } = {}) => {
  if (!date || date === "") return placeholder;
  const formatted = formatDate(date);
  const year = formatted.slice(-4);
  const dayMonth = formatted.slice(0, -4);
  return dayMonth + year;
};

export const ProratedPerspective = {
  ANNUALIZED: "annualized",
  PRORATED: "prorated",
};

export const prorate = (
  value,
  { originalPerspective, targetPerspective, proRataFactor = 1 }
) => {
  if (
    value == null ||
    originalPerspective === targetPerspective ||
    proRataFactor === 1
  ) {
    return value;
  }

  if (
    originalPerspective === ProratedPerspective.ANNUALIZED &&
    targetPerspective === ProratedPerspective.PRORATED
  ) {
    return value * proRataFactor;
  }

  if (
    originalPerspective === ProratedPerspective.PRORATED &&
    targetPerspective === ProratedPerspective.ANNUALIZED
  ) {
    return value / proRataFactor;
  }

  return value;
};

export const getProratedDisplayValues = (value, field, proRataFactor) => {
  if (
    value == null ||
    value === "" ||
    proRataFactor === 1 ||
    field.valueType !== "currency"
  ) {
    return {
      displayValue: value,
      helperValue: null,
    };
  }

  const fieldPerspective = field.perspective || ProratedPerspective.PRORATED;

  const displayValue = prorate(value, {
    originalPerspective: fieldPerspective,
    targetPerspective: ProratedPerspective.PRORATED,
    proRataFactor,
  });

  const helperValue =
    fieldPerspective === ProratedPerspective.ANNUALIZED
      ? value
      : prorate(value, {
          originalPerspective: ProratedPerspective.PRORATED,
          targetPerspective: ProratedPerspective.ANNUALIZED,
          proRataFactor,
        });

  return {
    displayValue,
    helperValue,
  };
};

/**
 * Auto-formats date input by adding delimiters as user types.
 * Strips non-numeric characters and formats as YYYY-MM-DD.
 * Example: typing "20231225" becomes "2023-12-25"
 */
export const handleDateInput = (inputValue, delimiter = "-") => {
  const numbersOnly = inputValue.replace(/[^\d]/g, "");

  let formatted = numbersOnly;
  if (numbersOnly.length >= 4) {
    formatted = numbersOnly.slice(0, 4);
    if (numbersOnly.length >= 6) {
      formatted +=
        delimiter +
        numbersOnly.slice(4, 6) +
        delimiter +
        numbersOnly.slice(6, 8);
    } else if (numbersOnly.length > 4) {
      formatted += delimiter + numbersOnly.slice(4);
    }
  }

  const endsWithDelimiter = inputValue.endsWith(delimiter);
  if (endsWithDelimiter && (formatted.length === 4 || formatted.length === 7)) {
    formatted += delimiter;
  }

  return formatted.slice(0, 10 + delimiter.length);
};
